import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import { fetchClient } from "../../API/fetchClient";

// Analytics Request Parameters
export interface AnalyticsFilters {
  from_period: string; // Format: "YYYY-MM"
  to_period: string; // Format: "YYYY-MM"
  site_id?: number | null;
  site_ids?: number[]; // Array of site IDs for multi-select
  bu_id?: number | null;
  bu_ids?: number[]; // Array of BU IDs for multi-select
  activite_id?: number | null;
  activite_ids?: number[]; // Array of activite IDs for multi-select
  filiere_id?: number | null;
  filiere_ids?: number[]; // Array of filiere IDs for multi-select
  region_id?: number | null;
  region_ids?: number[]; // Array of region IDs for multi-select
  champ_metier?: number | null; // Business field ID (required by API)
  dimension_type?: "produit" | "famille"; // Only for EAU module
  selections?: { dimension_id: number; valeur_id: number }[]; // Dimension value filters (e.g., energie_type=ELEC)
}

// Analytics Response Data Point (Time Series)
export interface AnalyticsDataPoint {
  month: string; // Changed from periode to match actual API
  valeur: number;
}

// Analytics Response Summary
export interface AnalyticsSummary {
  champ_metier_id: number;
  champ_metier_name: string;
  unit: string;
  total: number;
  avg: number;
}

// Analytics Response
export interface ModuleAnalyticsResponse {
  summary: AnalyticsSummary;
  time_series: AnalyticsDataPoint[]; // Changed from data to match actual API
  // Additional metadata
  filters?: AnalyticsFilters;
}

/**
 * Normalizes the response from either /core/saisie-analytics/ (summary is an object)
 * or /core/analytics/energie/ (summary is an array of KPI items with dynamic time_series keys).
 */
export function parseAnalyticsData(data: any): {
  total: number;
  unit: string;
  timeSeries: AnalyticsDataPoint[];
} {
  if (!data) return { total: 0, unit: "", timeSeries: [] };

  if (Array.isArray(data.summary)) {
    const total: number = data.summary.reduce(
      (acc: number, s: any) => acc + (s.total || 0),
      0
    );
    const unit: string = data.summary[0]?.unit || "";
    const timeSeries: AnalyticsDataPoint[] = (data.time_series || []).map(
      (entry: Record<string, string | number>) => {
        const valeur = Object.entries(entry)
          .filter(([k]) => k !== "month")
          .reduce((acc, [, v]) => acc + (typeof v === "number" ? v : 0), 0);
        return { month: entry.month as string, valeur };
      }
    );
    return { total, unit, timeSeries };
  }

  return {
    total: data.summary?.total || 0,
    unit: data.summary?.unit || "",
    timeSeries: data.time_series || [],
  };
}

interface UseGetModuleAnalyticsOptions
  extends Omit<UseQueryOptions<ModuleAnalyticsResponse, Error>, "queryKey" | "queryFn"> {
  filters: AnalyticsFilters;
  module?: "eau" | "energie" | "general"; // Module type
}

/**
 * Advanced Analytics Hook for EAU and ENERGIE modules
 * POST /core/saisie-analytics/
 *
 * Features:
 * - Filter by period range (from_period, to_period)
 * - Filter by organizational entities (BU, Activité, Filière, Région)
 * - Filter by site
 * - Filter by business field (champ_metier_id)
 * - For EAU module: specify dimension_type ("produit" or "famille")
 *
 * Example usage:
 * ```tsx
 * const { data } = useGetModuleAnalytics({
 *   filters: {
 *     from_period: "2024-01",
 *     to_period: "2024-12",
 *     site_id: 4,
 *     dimension_type: "produit" // Only for EAU module
 *   },
 *   module: "eau"
 * });
 * ```
 */
export const useGetModuleAnalytics = ({
  filters,
  module = "general",
  ...queryOptions
}: UseGetModuleAnalyticsOptions) => {
  return useQuery<ModuleAnalyticsResponse, Error>({
    queryKey: ["moduleAnalytics", module, filters],
    queryFn: async () => {
      console.log(
        `[useGetModuleAnalytics] Fetching ${module.toUpperCase()} analytics with filters:`,
        filters
      );

      // Validate required fields
      if (!filters.from_period || !filters.to_period) {
        throw new Error("from_period and to_period are required");
      }

      // For EAU module, validate dimension_type if provided
      if (
        module === "eau" &&
        filters.dimension_type &&
        !["produit", "famille"].includes(filters.dimension_type)
      ) {
        throw new Error('For EAU module, dimension_type must be "produit" or "famille"');
      }

      // Filter out null/undefined values from filters
      const cleanedFilters: Record<string, any> = {};
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          cleanedFilters[key] = value;
        }
      });

      const endpoint =
        module === "energie" ? `/core/analytics/energie/` : `/core/saisie-analytics/`;

      const response = await fetchClient<ModuleAnalyticsResponse>(endpoint, {
        method: "POST",
        body: cleanedFilters,
      });

      if (response.error) {
        console.error(`[useGetModuleAnalytics] API Error:`, response.error);

        if (response.error.detail) {
          throw new Error(response.error.detail);
        }

        throw new Error(`Failed to fetch ${module} analytics: ${JSON.stringify(response.error)}`);
      }

      if (!response.data) {
        console.warn(`[useGetModuleAnalytics] No data received`);
        return {
          summary: {
            champ_metier_id: 0,
            champ_metier_name: "",
            unit: "",
            total: 0,
            avg: 0,
          },
          time_series: [],
          filters,
        };
      }

      console.log(`[useGetModuleAnalytics] Successfully fetched analytics data for ${module}`);

      const { total, unit, timeSeries } = parseAnalyticsData(response.data);
      return {
        summary: {
          champ_metier_id: (response.data as any).summary?.champ_metier_id || 0,
          champ_metier_name: (response.data as any).summary?.champ_metier_name || "",
          unit,
          total,
          avg: (response.data as any).summary?.avg || 0,
        },
        time_series: timeSeries,
        filters,
      };
    },
    enabled: !!filters.from_period && !!filters.to_period && !!filters.champ_metier, // champ_metier is required by the API
    staleTime: 1000 * 60 * 5, // 5 minutes
    ...queryOptions,
  });
};

/**
 * Convenience hook specifically for EAU module analytics
 */
export const useGetEauAnalytics = (
  filters: AnalyticsFilters & { dimension_type: "produit" | "famille" },
  queryOptions?: Omit<UseQueryOptions<ModuleAnalyticsResponse, Error>, "queryKey" | "queryFn">
) => {
  return useGetModuleAnalytics({
    filters,
    module: "eau",
    ...queryOptions,
  });
};

/**
 * Convenience hook specifically for ENERGIE module analytics
 */
export const useGetEnergieAnalytics = (
  filters: AnalyticsFilters,
  queryOptions?: Omit<UseQueryOptions<ModuleAnalyticsResponse, Error>, "queryKey" | "queryFn">
) => {
  return useGetModuleAnalytics({
    filters,
    module: "energie",
    ...queryOptions,
  });
};
